import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  applyActionCode,
  confirmPasswordReset as fbConfirmPasswordReset,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  multiFactor,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyPasswordResetCode,
  type User,
} from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import type { OrgRole, OrgSummary, PlatformRole } from '@jam/shared';
import { auth } from '@/lib/firebase';
import { readActiveOrgId, writeActiveOrgId } from '@/lib/active-org';
import { listMemberships } from '@/lib/orgs-client';
import { logger } from '@/lib/logger';

/**
 * Active organization — the tenant all org-scoped reads/writes target.
 * The tuple of { id, name, role } is denormalized from the user's
 * memberships so pages can render without a second fetch.
 */
export interface ActiveOrg {
  id: string;
  name: string;
  role: OrgRole;
}

interface AuthContextValue {
  user: User | null;
  platformRole: PlatformRole | null;
  activeOrg: ActiveOrg | null;
  memberships: OrgSummary[];
  /** True when the signed-in user has at least one enrolled MFA factor. */
  hasMfa: boolean;
  loading: boolean;
  switchOrg: (orgId: string) => Promise<void>;
  refreshClaims: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  applyEmailVerification: (code: string) => Promise<void>;
  resendEmailVerification: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Read `platformRole` + `orgs` from the user's ID-token result. The old
 * single-tier `role` claim is gone — every org scopes its own `OrgRole`.
 */
function readClaims(tokenResult: {
  claims: Record<string, unknown>;
}): {
  platformRole: PlatformRole | null;
  orgsClaim: Record<string, OrgRole>;
} {
  const rawPlatform = tokenResult.claims['platformRole'];
  const platformRole: PlatformRole | null =
    rawPlatform === 'superAdmin' ? 'superAdmin' : null;

  const rawOrgs = tokenResult.claims['orgs'];
  const orgsClaim: Record<string, OrgRole> = {};
  if (rawOrgs && typeof rawOrgs === 'object') {
    for (const [id, role] of Object.entries(rawOrgs as Record<string, unknown>)) {
      if (role === 'admin' || role === 'member') {
        orgsClaim[id] = role;
      }
    }
  }
  return { platformRole, orgsClaim };
}

/**
 * Pick the active org id from (in order): localStorage preference if
 * still a valid membership, otherwise the first membership, otherwise
 * null.
 */
function pickActiveOrgId(
  memberships: OrgSummary[],
  preferredId: string | null,
): string | null {
  if (preferredId && memberships.some((m) => m.id === preferredId)) {
    return preferredId;
  }
  return memberships[0]?.id ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [platformRole, setPlatformRole] = useState<PlatformRole | null>(null);
  const [memberships, setMemberships] = useState<OrgSummary[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [hasMfa, setHasMfa] = useState(false);
  const [loading, setLoading] = useState(true);

  // Hydrate from Firebase auth state + custom claims + /api/orgs.
  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setPlatformRole(null);
        setMemberships([]);
        setActiveOrgId(null);
        writeActiveOrgId(null);
        setHasMfa(false);
        setLoading(false);
        return;
      }

      try {
        const tokenResult = await u.getIdTokenResult();
        const { platformRole: pRole, orgsClaim } = readClaims(tokenResult);
        setPlatformRole(pRole);
        setHasMfa(multiFactor(u).enrolledFactors.length > 0);

        // Claims give us the org-ids + roles cheaply. Fetch /api/orgs to
        // decorate with names and the (optional) primaryOrgId. When the
        // server isn't reachable yet (e.g. pre-deploy dogfood), fall
        // back to a synthetic list built from the claim so the UI can at
        // least render the switcher with ids.
        let list: OrgSummary[] = [];
        let primaryOrgId: string | null = null;
        try {
          const resp = await listMemberships();
          list = resp.organizations;
          primaryOrgId = resp.primaryOrgId ?? null;
        } catch (err) {
          logger.warn('auth: listMemberships failed, falling back to claim', {
            error: String(err),
          });
          list = Object.entries(orgsClaim).map(([id, role]) => ({
            id,
            name: id,
            slug: id,
            role,
          }));
        }
        setMemberships(list);

        const stored = readActiveOrgId();
        // `pickActiveOrgId` already falls back to `memberships[0]` when
        // the preferred id isn't a valid membership — so a single call
        // (stored → primary → first) is enough. No need to chain.
        const chosen = pickActiveOrgId(list, stored ?? primaryOrgId);
        setActiveOrgId(chosen);
        writeActiveOrgId(chosen);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const activeOrg: ActiveOrg | null = useMemo(() => {
    if (!activeOrgId) return null;
    const m = memberships.find((x) => x.id === activeOrgId);
    if (!m) return null;
    return { id: m.id, name: m.name, role: m.role };
  }, [activeOrgId, memberships]);

  const switchOrg = useCallback(
    async (orgId: string) => {
      const target = memberships.find((m) => m.id === orgId);
      if (!target) {
        throw new Error(`org ${orgId} is not a membership`);
      }
      writeActiveOrgId(orgId);
      setActiveOrgId(orgId);
      // Blow away cached per-tenant data so nothing from the old org
      // leaks into the new one before its own fetch lands.
      queryClient.invalidateQueries();
    },
    [memberships, queryClient],
  );

  const refreshClaims = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    const tokenResult = await current.getIdTokenResult(true);
    const { platformRole: pRole, orgsClaim } = readClaims(tokenResult);
    setPlatformRole(pRole);
    // Also refresh the membership list — a freshly-accepted invite
    // shows up here after the server stamps the claim.
    try {
      const resp = await listMemberships();
      setMemberships(resp.organizations);
      const stored = readActiveOrgId();
      const chosen = pickActiveOrgId(
        resp.organizations,
        stored ?? resp.primaryOrgId ?? null,
      );
      setActiveOrgId(chosen);
      writeActiveOrgId(chosen);
    } catch (err) {
      logger.warn('auth: refreshClaims listMemberships failed', {
        error: String(err),
      });
      // Fall back to the claim map if the server is unreachable.
      setMemberships(
        Object.entries(orgsClaim).map(([id, role]) => ({
          id,
          name: id,
          slug: id,
          role,
        })),
      );
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(cred.user);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    await signInWithPopup(auth, new GoogleAuthProvider());
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const confirmPasswordReset = useCallback(
    async (code: string, newPassword: string) => {
      await verifyPasswordResetCode(auth, code);
      await fbConfirmPasswordReset(auth, code, newPassword);
    },
    [],
  );

  const applyEmailVerification = useCallback(async (code: string) => {
    await applyActionCode(auth, code);
  }, []);

  const resendEmailVerification = useCallback(async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  }, []);

  const signOutUser = useCallback(async () => {
    await signOut(auth);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        platformRole,
        activeOrg,
        memberships,
        hasMfa,
        loading,
        switchOrg,
        refreshClaims,
        signIn,
        signUp,
        signInWithGoogle,
        sendPasswordReset,
        confirmPasswordReset,
        applyEmailVerification,
        resendEmailVerification,
        signOutUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Returns the active org, or throws if there is none. Use on pages that
 * sit behind the authed gate — by the time they mount, `requireAuth` has
 * already bounced users with zero memberships to `/onboarding/no-org`
 * and the switchOrg picker has selected one.
 */
export function useActiveOrg(): ActiveOrg {
  const { activeOrg } = useAuth();
  if (!activeOrg) {
    throw new Error(
      'useActiveOrg called with no active org — is this page behind requireAuth?',
    );
  }
  return activeOrg;
}

/** True for platform superadmins. */
export function useIsSuperAdmin(): boolean {
  const { platformRole } = useAuth();
  return platformRole === 'superAdmin';
}

/**
 * True for users who can manage the current org — either the active-org
 * admin or any platform superadmin. SuperAdmin escape hatches the org
 * admin gate (they can always manage any tenant by design).
 */
export function useIsOrgAdmin(): boolean {
  const { activeOrg, platformRole } = useAuth();
  if (platformRole === 'superAdmin') return true;
  return activeOrg?.role === 'admin';
}
